import {
  ArrowRight,
  Bell,
  CalendarDays,
  CalendarClock,
  CircleX,
  ClipboardList,
  CreditCard,
  Eye,
  FileText,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  PackageCheck,
  Send,
  ShoppingBag,
  Sparkles,
  Stethoscope,
  Trash2,
  UserCircle2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import PublicLayout from "../layout/PublicLayout";
import API from "../services/api";
import { clearPatientUser, getPatientUser } from "../utils/patientAuth";
import { firstValidationError, isFutureOrTodayDate } from "../utils/validation";

const tabs = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "appointments", label: "Appointment", icon: CalendarDays },
  { key: "notes", label: "Therapy", icon: FileText },
  { key: "sessions", label: "Session", icon: Stethoscope },
  { key: "payments", label: "Payment", icon: CreditCard },
  { key: "orders", label: "Order", icon: ShoppingBag },
];

const formatMoney = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const formatStatusLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase()) || "Pending";

const toTimeValue = (value) => {
  if (!value) {
    return Number.NaN;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

const formatDate = (value) => {
  if (!value) {
    return "Not added";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const revokeObjectUrls = (urlMap = {}) => {
  Object.values(urlMap).forEach((url) => {
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  });
};

const isInlinePreviewableTherapyItem = (item) =>
  item?.resourceType === "image" ||
  item?.resourceType === "gif" ||
  item?.resourceType === "video" ||
  item?.mimeType === "application/pdf";

const getStatusBadgeClass = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "approved":
    case "confirmed":
    case "active":
    case "done":
    case "completed":
      return "bg-emerald-50 text-emerald-700";
    case "rescheduled":
      return "bg-sky-50 text-sky-700";
    case "cancelled":
    case "inactive":
      return "bg-rose-50 text-rose-700";
    default:
      return "bg-amber-50 text-amber-700";
  }
};

const formatServiceLocation = (value) =>
  String(value || "").toLowerCase() === "home" ? "At home" : "At clinic";

const isOpenAppointmentStatus = (status) =>
  !["cancelled", "completed", "done"].includes(
    String(status || "pending").toLowerCase()
  );

const appointmentLocationNote =
  "Home service is available after OPW confirms suitability. First-time patients and every post-session review must visit the clinic.";

export default function PatientDashboardPage() {
  const navigate = useNavigate();
  const [patientUser, setPatientUser] = useState(() => getPatientUser());
  const [patient, setPatient] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [sendingAppointment, setSendingAppointment] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [activeFileAction, setActiveFileAction] = useState("");
  const [previewItem, setPreviewItem] = useState(null);
  const [therapyCardPreviewUrls, setTherapyCardPreviewUrls] = useState({});
  const [appointmentForm, setAppointmentForm] = useState({
    service: "",
    serviceLocation: "clinic",
    date: "",
    time: "",
    message: "",
    files: [],
  });
  const [appointmentRequests, setAppointmentRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [shopOrders, setShopOrders] = useState([]);
  const [services, setServices] = useState([]);

  const patientId = patientUser?.patientId || "";
  const clinicalNotes = useMemo(() => patient?.clinicalNotes || [], [patient?.clinicalNotes]);
  const therapyRecommendations = useMemo(
    () => patient?.therapyRecommendations || [],
    [patient?.therapyRecommendations]
  );
  const appointments = useMemo(() => patient?.appointments || [], [patient?.appointments]);
  const treatmentPlans = useMemo(() => patient?.treatmentPlans || [], [patient?.treatmentPlans]);
  const directPayments = useMemo(() => patient?.payments || [], [patient?.payments]);

  const loadPatient = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [patientResponse, requestResponse, ordersResponse, notificationsResponse] = await Promise.all([
        API.get(`/patients/${patientId}`),
        API.get(`/patients/${patientId}/appointment-requests`),
        API.get("/shop/orders/my"),
        API.get(`/patients/${patientId}/notifications`),
      ]);
      setPatient(patientResponse.data);
      setAppointmentRequests(requestResponse.data || []);
      setShopOrders(ordersResponse.data || []);
      setNotifications(notificationsResponse.data || []);
      setStatus({ type: "", message: "" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to load patient dashboard.",
      });
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (!patientUser) {
      navigate("/patient-login?redirect=/patient-dashboard", { replace: true });
      return;
    }

    loadPatient();
  }, [loadPatient, navigate, patientUser]);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await API.get("/services");
        setServices(response.data || []);
      } catch (_) {
        setServices([]);
      }
    };

    loadServices();
  }, []);

  useEffect(() => {
    if (activeTab !== "notes" || !therapyRecommendations.length) {
      setTherapyCardPreviewUrls((current) => {
        revokeObjectUrls(current);
        return {};
      });
      return undefined;
    }

    const previewItems = therapyRecommendations
      .flatMap((recommendation) => recommendation.items || [])
      .filter(isInlinePreviewableTherapyItem);

    if (!previewItems.length) {
      setTherapyCardPreviewUrls((current) => {
        revokeObjectUrls(current);
        return {};
      });
      return undefined;
    }

    let disposed = false;

    const loadTherapyCardPreviews = async () => {
      const nextUrls = {};

      await Promise.all(
        previewItems.map(async (item) => {
          try {
            const response = await API.get(item.fileUrl, { responseType: "blob" });
            nextUrls[item.id] = window.URL.createObjectURL(response.data);
          } catch (_) {
            nextUrls[item.id] = "";
          }
        })
      );

      if (disposed) {
        revokeObjectUrls(nextUrls);
        return;
      }

      setTherapyCardPreviewUrls((current) => {
        revokeObjectUrls(current);
        return nextUrls;
      });
    };

    loadTherapyCardPreviews();

    return () => {
      disposed = true;
    };
  }, [activeTab, therapyRecommendations]);

  useEffect(
    () => () => {
      revokeObjectUrls(therapyCardPreviewUrls);
    },
    [therapyCardPreviewUrls]
  );

  const bookedAppointmentRequestIds = new Set(
    appointments.map((appointment) => appointment.requestId).filter(Boolean)
  );
  const visibleAppointmentRequests = appointmentRequests.filter(
    (request) =>
      !bookedAppointmentRequestIds.has(request.id) &&
      !["cancelled", "completed"].includes(request.status || "")
  );
  const activeAppointmentRequests = visibleAppointmentRequests.filter((request) =>
    isOpenAppointmentStatus(request.status)
  );
  const activeAppointments = appointments.filter((appointment) =>
    isOpenAppointmentStatus(appointment.status || "approved")
  );
  const hasOpenAppointmentFlow =
    activeAppointmentRequests.length > 0 || activeAppointments.length > 0;
  const activeTreatmentPlan = treatmentPlans.find(
    (plan) => (plan.status || "active") === "active"
  );
  const hasActiveTreatmentSession = Boolean(activeTreatmentPlan);
  const activeTreatmentAppointmentMessage =
    "Your treatment session is active. A new appointment can be requested only after the current treatment session is completed.";
  const hasPendingAppointmentRequest = visibleAppointmentRequests.some(
    (request) => (request.status || "pending") === "pending"
  );
  const therapyItemCount = therapyRecommendations.reduce(
    (sum, recommendation) => sum + (recommendation.items?.length || 0),
    0
  );
  const treatmentPayments = treatmentPlans.flatMap((plan) =>
    (plan.payments || []).map((payment) => ({
      ...payment,
      treatmentName: Array.isArray(plan.treatmentTypes)
        ? plan.treatmentTypes.join(", ")
        : "Treatment",
    }))
  );
  const payments = [...directPayments, ...treatmentPayments];

  const stats = useMemo(
    () => [
      {
        label: "Therapy Items",
        value: therapyItemCount,
        icon: FolderOpen,
        tabKey: "notes",
        detail: "View your assigned therapy files",
        tone: "bg-sky-50 text-sky-700",
      },
      {
        label: "Appointments",
        value: appointments.length + visibleAppointmentRequests.length,
        icon: CalendarDays,
        tabKey: "appointments",
        detail: hasActiveTreatmentSession
          ? "Treatment session is active"
          : hasOpenAppointmentFlow
          ? "Current appointment/request is active"
          : "Track requests and booked visits",
        tone: "bg-violet-50 text-violet-700",
      },
      {
        label: "Sessions",
        value: treatmentPlans.length,
        icon: Stethoscope,
        tabKey: "sessions",
        detail: "Check your treatment and session plan",
        tone: "bg-emerald-50 text-emerald-700",
      },
      {
        label: "Payments",
        value: payments.length,
        icon: CreditCard,
        tabKey: "payments",
        detail: "Review all recorded OPW payments",
        tone: "bg-amber-50 text-amber-700",
      },
      {
        label: "Orders",
        value: shopOrders.length,
        icon: ShoppingBag,
        tabKey: "orders",
        detail: "Track shop purchases from your account",
        tone: "bg-cyan-50 text-cyan-700",
      },
    ],
    [
      appointments.length,
      hasActiveTreatmentSession,
      hasOpenAppointmentFlow,
      payments.length,
      shopOrders.length,
      therapyItemCount,
      treatmentPlans.length,
      visibleAppointmentRequests.length,
    ]
  );
  const pendingRequestCount = visibleAppointmentRequests.filter(
    (request) => (request.status || "pending") === "pending"
  ).length;
  const totalPaidAmount = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );
  const outstandingBalance = treatmentPlans.reduce(
    (sum, plan) => sum + Number(plan.balanceAmount || 0),
    0
  );
  const totalSessionDays = treatmentPlans.reduce(
    (sum, plan) => sum + (plan.sessionDays?.length || 0),
    0
  );
  const completedSessionDays = treatmentPlans.reduce(
    (sum, plan) =>
      sum + (plan.sessionDays || []).filter((day) => day.status === "done").length,
    0
  );
  const sessionCompletionPercent = totalSessionDays
    ? Math.min(100, Math.round((completedSessionDays / totalSessionDays) * 100))
    : 0;
  const nextBookedAppointment = [...appointments]
    .filter((appointment) => !["completed", "cancelled"].includes(appointment.status || ""))
    .sort((a, b) => {
      const first = toTimeValue(a.date);
      const second = toTimeValue(b.date);
      return (Number.isNaN(first) ? Number.MAX_SAFE_INTEGER : first) -
        (Number.isNaN(second) ? Number.MAX_SAFE_INTEGER : second);
    })[0];
  const latestPayment = [...payments].sort((a, b) => {
    const first = toTimeValue(a.createdAt);
    const second = toTimeValue(b.createdAt);
    return (Number.isNaN(second) ? 0 : second) - (Number.isNaN(first) ? 0 : first);
  })[0];
  const latestOrder = [...shopOrders].sort((a, b) => {
    const first = toTimeValue(a.createdAt);
    const second = toTimeValue(b.createdAt);
    return (Number.isNaN(second) ? 0 : second) - (Number.isNaN(first) ? 0 : first);
  })[0];
  const latestClinicalNote = [...clinicalNotes].sort((a, b) => {
    const first = toTimeValue(a.createdAt);
    const second = toTimeValue(b.createdAt);
    return (Number.isNaN(second) ? 0 : second) - (Number.isNaN(first) ? 0 : first);
  })[0];
  const tabCounts = {
    overview: 0,
    appointments: appointments.length + visibleAppointmentRequests.length,
    notes: therapyItemCount + clinicalNotes.length,
    sessions: treatmentPlans.length,
    payments: payments.length,
    orders: shopOrders.length,
  };
  const quickActions = [
    {
      key: "appointments",
      label: "Book Appointment",
      helper: hasOpenAppointmentFlow ? "Current request is active" : "Start a new request",
      icon: CalendarClock,
    },
    {
      key: "notes",
      label: "Open Therapy",
      helper: `${therapyItemCount} therapy item${therapyItemCount === 1 ? "" : "s"} shared`,
      icon: Sparkles,
    },
    {
      key: "payments",
      label: "Check Payments",
      helper: `${formatMoney(totalPaidAmount)} recorded`,
      icon: CreditCard,
    },
    {
      key: "orders",
      label: "View Orders",
      helper: `${shopOrders.length} shop order${shopOrders.length === 1 ? "" : "s"}`,
      icon: PackageCheck,
    },
  ];

  const handleLogout = () => {
    clearPatientUser();
    setPatientUser(null);
    navigate("/patient-login?redirect=/patient-dashboard", { replace: true });
  };

  const unreadNotifications = notifications.filter((item) => !item.readAt);

  const markNotificationRead = async (notificationId) => {
    try {
      await API.patch(`/patients/${patientId}/notifications/${notificationId}/read`);
      setNotifications((current) =>
        current.map((item) =>
          item.id === notificationId ? { ...item, readAt: new Date().toISOString() } : item
        )
      );
    } catch (_) {
      setStatus({ type: "error", message: "Failed to update notification." });
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const response = await API.patch(`/patients/${patientId}/notifications/read-all`);
      setNotifications(response.data || []);
    } catch (_) {
      setStatus({ type: "error", message: "Failed to update notifications." });
    }
  };

  const handleAppointmentSubmit = async (event) => {
    event.preventDefault();

    const validationError = firstValidationError([
      hasActiveTreatmentSession ? activeTreatmentAppointmentMessage : "",
      hasOpenAppointmentFlow
        ? "You already have an active appointment request. You can send another after it is done or cancelled."
        : "",
      !appointmentForm.service.trim() ? "Please add service." : "",
      !appointmentForm.date ? "Please add preferred date." : "",
      appointmentForm.date && !isFutureOrTodayDate(appointmentForm.date)
        ? "Preferred date cannot be in the past."
        : "",
    ]);

    if (validationError) {
      setStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setSendingAppointment(true);
      const payload = new FormData();
      payload.append("name", patientUser.name || "");
      payload.append("email", patientUser.email || "");
      payload.append("phone", patientUser.mobile || "");
      payload.append("patientId", patientId);
      payload.append("service", appointmentForm.service.trim());
      payload.append("serviceLocation", appointmentForm.serviceLocation || "clinic");
      payload.append("date", appointmentForm.date);
      payload.append("time", appointmentForm.time);
      payload.append("message", appointmentForm.message.trim());
      appointmentForm.files.forEach((file) => payload.append("files", file));

      const response = await API.post("/appointments", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setAppointmentForm({
        service: "",
        serviceLocation: "clinic",
        date: "",
        time: "",
        message: "",
        files: [],
      });
      setStatus({
        type: "success",
        message: response.data?.message || "Appointment request submitted.",
      });
      loadPatient();
    } catch (error) {
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to submit appointment request.",
      });
    } finally {
      setSendingAppointment(false);
    }
  };

  const handleAppointmentFilesChange = (event) => {
    const nextFiles = Array.from(event.target.files || []);

    if (!nextFiles.length) {
      return;
    }

    setAppointmentForm((current) => {
      const mergedFiles = [...current.files];

      nextFiles.forEach((file) => {
        const exists = mergedFiles.some(
          (entry) =>
            entry.name === file.name &&
            entry.size === file.size &&
            entry.lastModified === file.lastModified
        );

        if (!exists) {
          mergedFiles.push(file);
        }
      });

      return {
        ...current,
        files: mergedFiles,
      };
    });

    event.target.value = "";
  };

  const handleRemoveAppointmentFile = (fileToRemove) => {
    setAppointmentForm((current) => ({
      ...current,
      files: current.files.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      ),
    }));
  };

  const handleTherapyFileAction = async (item, action) => {
    const actionKey = `${action}:${item.id}`;

    try {
      setActiveFileAction(actionKey);
      setStatus({ type: "", message: "" });

      const response = await API.get(item.fileUrl, { responseType: "blob" });
      const objectUrl = window.URL.createObjectURL(response.data);

      setPreviewItem({
        ...item,
        objectUrl,
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          "Failed to open therapy file.",
      });
    } finally {
      setActiveFileAction("");
    }
  };

  const closePreview = () => {
    if (previewItem?.objectUrl) {
      window.URL.revokeObjectURL(previewItem.objectUrl);
    }

    setPreviewItem(null);
  };

  if (!patientUser) {
    return null;
  }

  return (
    <PublicLayout>
      <Seo
        title="My Account"
        description="View your OPW patient account with appointments, therapy, sessions, payments, and orders."
        path="/patient-dashboard"
        robots="noindex, nofollow"
      />
      <section className="page-section mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-800 px-6 py-5 text-white shadow-[0_18px_45px_rgba(15,23,42,0.18)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_34%)]" />
          <div className="relative z-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-white/60">
                My Account
              </p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight">
                Welcome, {patientUser.name || "Patient"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/75">
                View your notes, appointment requests, treatment sessions, and payment updates from OPW.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-white/90">
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5">
                  {pendingRequestCount} pending request{pendingRequestCount === 1 ? "" : "s"}
                </span>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5">
                  {therapyItemCount} therapy item{therapyItemCount === 1 ? "" : "s"}
                </span>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5">
                  {formatMoney(outstandingBalance)} balance
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowNotifications(true)}
                className="relative inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
              >
                <Bell size={16} />
                Notifications
                {unreadNotifications.length ? (
                  <span className="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-black text-white">
                    {unreadNotifications.length}
                  </span>
                ) : null}
              </button>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-sm text-white/90">
                <UserCircle2 size={18} />
                {patientUser.email}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-slate-100"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>

        {showNotifications ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 px-4 py-5 sm:items-center">
            <div className="max-h-[86vh] w-full max-w-2xl overflow-hidden rounded-[28px] bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                    OPW Updates
                  </p>
                  <h2 className="text-xl font-black text-slate-950">Notifications</h2>
                </div>
                <div className="flex items-center gap-2">
                  {unreadNotifications.length ? (
                    <button
                      type="button"
                      onClick={markAllNotificationsRead}
                      className="rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700"
                    >
                      Mark all read
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setShowNotifications(false)}
                    className="rounded-full bg-slate-950 px-3 py-2 text-xs font-bold text-white"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="max-h-[68vh] space-y-3 overflow-auto p-5">
                {unreadNotifications.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                    No new notifications. Appointment, treatment, therapy, payment, and custom OPW updates will appear here.
                  </p>
                ) : (
                  unreadNotifications.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => markNotificationRead(item.id)}
                      className="w-full rounded-2xl border border-sky-200 bg-sky-50 p-4 text-left transition hover:border-sky-300"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-black text-slate-950">{item.title}</p>
                          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                            {formatDate(item.scheduledFor || item.createdAt)} | {item.category || "update"}
                          </p>
                        </div>
                        <span className="rounded-full bg-sky-600 px-2.5 py-1 text-xs font-bold text-white">
                          New
                        </span>
                      </div>
                      <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">
                        {item.body}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}

        {status.message ? (
          <div
            className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
              status.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            {status.message}
          </div>
        ) : null}

        <div className="mt-5 overflow-x-auto rounded-[22px] border border-slate-200 bg-white/90 p-1.5 shadow-sm">
          <div className="flex min-w-max gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                    active
                      ? "bg-slate-950 text-white shadow-lg shadow-slate-900/15"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon size={17} />
                  {tab.label}
                  {tabCounts[tab.key] ? (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {tabCounts[tab.key]}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="rounded-[26px] border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Loading patient dashboard...
            </div>
          ) : (
            <>
              {activeTab === "overview" && (
                <div className="space-y-5">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                    {stats.map((stat) => {
                      const Icon = stat.icon;
                      return (
                        <button
                          key={stat.label}
                          type="button"
                          onClick={() => setActiveTab(stat.tabKey)}
                          className="group rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className={`inline-flex rounded-2xl p-2.5 ${stat.tone}`}>
                              <Icon size={20} />
                            </div>
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                              Open
                            </span>
                          </div>
                          <p className="mt-5 text-3xl font-semibold text-slate-950">{stat.value}</p>
                          <p className="mt-2 text-sm font-medium text-slate-700">{stat.label}</p>
                          <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-500">
                            <span>{stat.detail}</span>
                            <ArrowRight
                              size={15}
                              className="shrink-0 transition group-hover:translate-x-0.5"
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
                    <Panel
                      title="Recovery Journey"
                      subtitle="A quick view of your current recovery progress and important updates."
                    >
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(135deg,#eff6ff,#f8fbff)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Next Visit
                          </p>
                          <p className="mt-3 text-lg font-semibold text-slate-950">
                            {nextBookedAppointment?.service || "Not scheduled"}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {nextBookedAppointment
                              ? `${formatDate(nextBookedAppointment.date)}${
                                  nextBookedAppointment.time ? ` at ${nextBookedAppointment.time}` : ""
                                }`
                              : "Your next confirmed appointment will show here."}
                          </p>
                        </div>

                        <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(135deg,#f0fdf4,#f8fffb)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Session Progress
                          </p>
                          <p className="mt-3 text-lg font-semibold text-slate-950">
                            {completedSessionDays}/{totalSessionDays || 0} completed
                          </p>
                          <div className="mt-3 h-2.5 rounded-full bg-white/70">
                            <div
                              className="h-2.5 rounded-full bg-emerald-500"
                              style={{ width: `${sessionCompletionPercent}%` }}
                            />
                          </div>
                          <p className="mt-2 text-sm text-slate-500">
                            {sessionCompletionPercent}% of planned session days are marked done.
                          </p>
                        </div>

                        <div className="rounded-[22px] border border-slate-200 bg-[linear-gradient(135deg,#fff7ed,#fffdf8)] p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Payment Snapshot
                          </p>
                          <p className="mt-3 text-lg font-semibold text-slate-950">
                            {formatMoney(totalPaidAmount)}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            Paid so far | Balance due {formatMoney(outstandingBalance)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 grid gap-3 md:grid-cols-3">
                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Requests Waiting
                          </p>
                          <p className="mt-2 text-2xl font-semibold text-slate-950">
                            {pendingRequestCount}
                          </p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Shared Notes
                          </p>
                          <p className="mt-2 text-2xl font-semibold text-slate-950">
                            {clinicalNotes.length}
                          </p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Shop Orders
                          </p>
                          <p className="mt-2 text-2xl font-semibold text-slate-950">
                            {shopOrders.length}
                          </p>
                        </div>
                      </div>
                    </Panel>

                    <div className="grid gap-5">
                      <Panel
                        title="Quick Actions"
                        subtitle="Jump straight to the section you want without scrolling through the whole dashboard."
                      >
                        <div className="grid gap-3">
                          {quickActions.map((action) => {
                            const Icon = action.icon;
                            return (
                              <button
                                key={action.key}
                                type="button"
                                onClick={() => setActiveTab(action.key)}
                                className="group flex items-center justify-between gap-4 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-left transition hover:border-sky-200 hover:bg-white"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="rounded-2xl bg-white p-2.5 text-sky-700 shadow-sm">
                                    <Icon size={18} />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-950">{action.label}</p>
                                    <p className="mt-1 text-sm text-slate-500">{action.helper}</p>
                                  </div>
                                </div>
                                <ArrowRight
                                  size={16}
                                  className="shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700"
                                />
                              </button>
                            );
                          })}
                        </div>
                      </Panel>

                      <Panel
                        title="Recent Highlights"
                        subtitle="The latest update from your care plan, payments, and shop activity."
                      >
                        <div className="grid gap-3">
                          <HighlightCard
                            icon={ClipboardList}
                            title="Latest Doctor Note"
                            value={latestClinicalNote?.title || "No doctor note yet"}
                            helper={
                              latestClinicalNote
                                ? `Added on ${formatDate(latestClinicalNote.createdAt)}`
                                : "Notes shared by OPW will appear here."
                            }
                          />
                          <HighlightCard
                            icon={CreditCard}
                            title="Latest Payment"
                            value={latestPayment ? formatMoney(latestPayment.amount) : "No payment yet"}
                            helper={
                              latestPayment
                                ? `${latestPayment.method || "Payment"} | ${formatDate(latestPayment.createdAt)}`
                                : "Your newest recorded payment will appear here."
                            }
                          />
                          <HighlightCard
                            icon={PackageCheck}
                            title="Latest Order"
                            value={latestOrder?.orderNumber || "No order yet"}
                            helper={
                              latestOrder
                                ? `${formatStatusLabel(latestOrder.status)} | ${formatDate(latestOrder.createdAt)}`
                                : "Shop activity will appear here after your first order."
                            }
                          />
                        </div>
                      </Panel>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "notes" && (
                <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
                  <Panel title="Recommended Therapy" subtitle="Service-wise therapy shared by OPW for this patient.">
                    {!therapyRecommendations.length ? (
                      <div className="rounded-2xl bg-slate-50 px-4 py-5 text-sm leading-6 text-slate-500">
                        No therapy recommendations available yet.
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {therapyRecommendations.map((recommendation) => (
                          <div
                            key={recommendation.id}
                            className="overflow-hidden rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] shadow-sm"
                          >
                            <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.12),transparent_40%),linear-gradient(135deg,#f8fbff,#eef6ff)] px-5 py-4">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700/70">
                                    Therapy Service
                                  </p>
                                  <h3 className="mt-1 text-lg font-semibold text-slate-950">
                                    {recommendation.serviceName || "Therapy Service"}
                                  </h3>
                                  <p className="mt-1 text-sm text-slate-500">
                                    Updated on {formatDate(recommendation.updatedAt || recommendation.createdAt)}
                                  </p>
                                </div>
                                <span className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                                  {(recommendation.items || []).length} item
                                  {(recommendation.items || []).length === 1 ? "" : "s"}
                                </span>
                              </div>

                              {recommendation.note ? (
                                <p className="mt-4 rounded-2xl bg-white/90 px-4 py-3 text-sm leading-6 text-slate-700 shadow-sm">
                                  {recommendation.note}
                                </p>
                              ) : null}
                            </div>

                            <div className="grid gap-3 p-4 sm:grid-cols-2">
                              {(recommendation.items || []).map((item) => (
                                <div
                                  key={item.id}
                                  className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm"
                                >
                                  <div className="mb-4 overflow-hidden rounded-[20px] border border-slate-200 bg-slate-100">
                                    {therapyCardPreviewUrls[item.id] &&
                                    (item.resourceType === "image" ||
                                      item.resourceType === "gif") ? (
                                      <img
                                        src={therapyCardPreviewUrls[item.id]}
                                        alt={item.title || item.fileName || "Therapy Item"}
                                        className="h-48 w-full object-cover"
                                      />
                                    ) : null}

                                    {therapyCardPreviewUrls[item.id] &&
                                    item.resourceType === "video" ? (
                                      <video
                                        src={therapyCardPreviewUrls[item.id]}
                                        controls
                                        preload="metadata"
                                        className="h-48 w-full bg-black object-cover"
                                      />
                                    ) : null}

                                    {therapyCardPreviewUrls[item.id] &&
                                    item.mimeType === "application/pdf" ? (
                                      <iframe
                                        src={therapyCardPreviewUrls[item.id]}
                                        title={item.title || item.fileName || "Therapy PDF"}
                                        className="h-48 w-full bg-white"
                                      />
                                    ) : null}

                                    {!therapyCardPreviewUrls[item.id] ? (
                                      <div className="flex h-48 flex-col items-center justify-center gap-3 px-4 text-center">
                                        <div className="rounded-2xl bg-white p-4 text-slate-700 shadow-sm">
                                          <FileText size={28} />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-slate-900">
                                            {isInlinePreviewableTherapyItem(item)
                                              ? "Loading preview"
                                              : "Document file"}
                                          </p>
                                          <p className="mt-1 text-xs text-slate-500">
                                            {isInlinePreviewableTherapyItem(item)
                                              ? "Preparing secure preview..."
                                              : "Preview will open in the viewer"}
                                          </p>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-semibold text-slate-950">
                                          {item.title || item.fileName || "Therapy Item"}
                                        </p>
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-slate-600">
                                          {item.resourceType || "file"}
                                        </span>
                                      </div>
                                      <p className="mt-1 truncate text-xs text-slate-500">
                                        {item.fileName || "No file name"}
                                      </p>
                                    </div>
                                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700">
                                      <FolderOpen size={18} />
                                    </div>
                                  </div>

                                  <p className="mt-3 min-h-[48px] text-sm leading-6 text-slate-600">
                                    {item.description || "Therapy content shared by OPW for your recovery plan."}
                                  </p>

                                  <div className="mt-4 flex items-center justify-between">
                                    <span className="text-xs font-medium text-slate-400">
                                      View only
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleTherapyFileAction(item, "view")}
                                      className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                                    >
                                      <Eye size={14} />
                                      View
                                    </button>
                                  </div>

                                  {activeFileAction === `view:${item.id}` ||
                                  activeFileAction === `download:${item.id}` ? (
                                    <p className="mt-2 text-xs font-medium text-slate-500">
                                      Working...
                                    </p>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Panel>
                  <Panel title="Doctors Note & Documents" subtitle="You can only view notes and files shared here.">
                    <RecordList emptyText="No doctor notes available yet.">
                      {clinicalNotes.map((note) => (
                        <RecordCard key={note.id || note._id} title={note.title || "Doctors Note"} subtitle={note.note || "No note details added."}>
                          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Added by {note.addedByLabel || (note.addedByType === "patient" ? "Patient" : "OPW")}
                          </p>
                          {(note.documents || []).length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {note.documents.map((document) => (
                                <span key={document.id || document.name} className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700">
                                  {document.name || "Document"}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </RecordCard>
                      ))}
                    </RecordList>
                  </Panel>
                </div>
              )}

              {activeTab === "appointments" && (
                <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                  <Panel
                    title="Request Appointment"
                    subtitle="Only logged-in patients can submit this request. Upload PDF/images and previous doctor notes."
                  >
                    <div className="mb-4 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-2xl bg-slate-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Pending
                        </p>
                        <p className="mt-2 text-xl font-semibold text-slate-950">
                          {pendingRequestCount}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Booked
                        </p>
                        <p className="mt-2 text-xl font-semibold text-slate-950">
                          {appointments.length}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Next Visit
                        </p>
                        <p className="mt-2 text-sm font-semibold text-slate-950">
                          {nextBookedAppointment ? formatDate(nextBookedAppointment.date) : "Not set"}
                        </p>
                      </div>
                    </div>
                    {hasActiveTreatmentSession ? (
                      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold leading-6 text-emerald-800">
                        {activeTreatmentAppointmentMessage}
                      </div>
                    ) : hasOpenAppointmentFlow ? (
                      <div className="grid gap-3">
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-6 text-amber-800">
                          You already have an active appointment request or booked appointment. A new request can be created after OPW marks this one done or cancelled.
                        </div>
                        {activeAppointmentRequests.map((request) => (
                          <RecordCard
                            key={`active-request-${request.id}`}
                            title={request.service || "Appointment"}
                            subtitle={`Requested: ${formatDate(request.requestedDate)}${request.requestedTime ? ` at ${request.requestedTime}` : ""} | ${formatServiceLocation(request.serviceLocation)}`}
                          >
                            <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(request.status)}`}>
                              {formatStatusLabel(request.status || "pending")}
                            </span>
                            {request.decisionNote ? (
                              <p className="mt-2 text-sm text-slate-500">
                                OPW note: {request.decisionNote}
                              </p>
                            ) : null}
                          </RecordCard>
                        ))}
                        {activeAppointments.map((appointment) => (
                          <RecordCard
                            key={`active-appointment-${appointment.id || appointment._id}`}
                            title={appointment.service || "Appointment"}
                            subtitle={`${formatDate(appointment.date)}${appointment.time ? ` at ${appointment.time}` : ""} | ${formatServiceLocation(appointment.serviceLocation)}`}
                          >
                            <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(appointment.status)}`}>
                              {appointment.status === "completed"
                                ? "Done"
                                : formatStatusLabel(appointment.status || "approved")}
                            </span>
                            {appointment.remark ? (
                              <p className="mt-2 text-sm text-slate-500">
                                OPW note: {appointment.remark}
                              </p>
                            ) : null}
                          </RecordCard>
                        ))}
                      </div>
                    ) : (
                    <form onSubmit={handleAppointmentSubmit} className="grid gap-4">
                      <select
                        className="input rounded-2xl border-slate-200 bg-slate-50"
                        value={appointmentForm.service}
                        onChange={(event) => setAppointmentForm({ ...appointmentForm, service: event.target.value })}
                        disabled={hasPendingAppointmentRequest}
                        required
                      >
                        <option value="">Select service</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.name}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                          Service location
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {[
                            ["clinic", "At clinic"],
                            ["home", "At home"],
                          ].map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() =>
                                setAppointmentForm((current) => ({
                                  ...current,
                                  serviceLocation: value,
                                }))
                              }
                              disabled={hasPendingAppointmentRequest}
                              className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                                appointmentForm.serviceLocation === value
                                  ? "border-sky-300 bg-sky-50 text-sky-800"
                                  : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-white"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <p className="mt-3 rounded-2xl bg-sky-50 px-3 py-2 text-xs font-semibold leading-5 text-sky-800">
                          {appointmentLocationNote}
                        </p>
                      </div>
                      <input
                        type="date"
                        className="input rounded-2xl border-slate-200 bg-slate-50"
                        value={appointmentForm.date}
                        onChange={(event) => setAppointmentForm({ ...appointmentForm, date: event.target.value })}
                        disabled={hasPendingAppointmentRequest}
                        required
                      />
                      <input
                        type="time"
                        className="input rounded-2xl border-slate-200 bg-slate-50"
                        value={appointmentForm.time}
                        onChange={(event) => setAppointmentForm({ ...appointmentForm, time: event.target.value })}
                        disabled={hasPendingAppointmentRequest}
                      />
                      <textarea
                        className="input min-h-[140px] rounded-2xl border-slate-200 bg-slate-50"
                        placeholder="Tell us about your pain, injury, or preferred timing"
                        value={appointmentForm.message}
                        onChange={(event) => setAppointmentForm({ ...appointmentForm, message: event.target.value })}
                        disabled={hasPendingAppointmentRequest}
                      />
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                        <label className="mb-3 block text-sm font-medium text-slate-700">
                          Upload PDF/images and previous doctor notes
                        </label>
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800">
                          Upload Documents
                          <input
                            type="file"
                            multiple
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={handleAppointmentFilesChange}
                            disabled={hasPendingAppointmentRequest}
                          />
                        </label>
                        <p className="mt-2 text-xs text-slate-500">
                          Multiple documents/images can be added.
                        </p>
                        {appointmentForm.files.length ? (
                          <div className="mt-3 grid gap-2">
                            {appointmentForm.files.map((file) => (
                              <div
                                key={`${file.name}-${file.size}`}
                                className="flex items-center justify-between gap-3 rounded-2xl border border-sky-100 bg-white px-3 py-2 text-xs font-medium text-sky-700"
                              >
                                <span className="truncate">{file.name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAppointmentFile(file)}
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                                  title="Remove file"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <button disabled={sendingAppointment || hasPendingAppointmentRequest} className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-medium text-white disabled:opacity-60">
                        <Send size={18} />
                        {sendingAppointment ? "Sending..." : "Request Appointment"}
                      </button>
                    </form>
                    )}
                  </Panel>
                  <div className="grid gap-5">
                    <Panel title="Appointment Request Updates" subtitle="Pending requests stay here; approved ones move to booked appointments.">
                      <RecordList emptyText="No pending appointment request updates.">
                        {visibleAppointmentRequests.map((request) => (
                          <RecordCard
                            key={request.id}
                            title={request.service || "Appointment"}
                            subtitle={`Requested: ${formatDate(request.requestedDate)}${request.requestedTime ? ` at ${request.requestedTime}` : ""} | ${formatServiceLocation(request.serviceLocation)}`}
                          >
                            <div className="mt-2">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(request.status)}`}>
                                {formatStatusLabel(request.status || "pending")}
                              </span>
                            </div>
                            {request.status !== "pending" ? (
                              <p className="mt-2 rounded-2xl bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700">
                                {request.status === "rescheduled" ? "Rescheduled" : "Confirmed"}:{" "}
                                {formatDate(request.confirmedDate)}
                                {request.confirmedTime ? ` at ${request.confirmedTime}` : ""}
                              </p>
                            ) : null}
                            {request.decisionNote ? (
                              <p className="mt-2 text-sm text-slate-500">
                                OPW note: {request.decisionNote}
                              </p>
                            ) : null}
                          </RecordCard>
                        ))}
                      </RecordList>
                    </Panel>
                    <Panel title="Booked Appointments" subtitle="Appointments confirmed by OPW will appear here.">
                      <RecordList emptyText="No booked appointments yet.">
                        {appointments.map((appointment) => (
                          <RecordCard
                            key={appointment.id || appointment._id}
                            title={appointment.service || "Appointment"}
                            subtitle={`${formatDate(appointment.date)}${appointment.time ? ` at ${appointment.time}` : ""} | ${formatServiceLocation(appointment.serviceLocation)}`}
                          >
                            <div className="mt-2">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(appointment.status)}`}>
                                {appointment.status === "completed"
                                  ? "Done"
                                  : formatStatusLabel(appointment.status || "approved")}
                              </span>
                            </div>
                            {appointment.remark ? (
                              <p className="mt-2 rounded-2xl bg-sky-50 px-3 py-2 text-sm text-sky-700">
                                OPW remark: {appointment.remark}
                              </p>
                            ) : null}
                          </RecordCard>
                        ))}
                      </RecordList>
                    </Panel>
                  </div>
                </div>
              )}

              {activeTab === "sessions" && (
                <Panel title="Session / Treatment Details" subtitle="Treatment and session details from OPW.">
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Plans
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{treatmentPlans.length}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Completed Days
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{completedSessionDays}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Progress
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{sessionCompletionPercent}%</p>
                    </div>
                  </div>
                  <RecordList emptyText="No session details added yet.">
                    {treatmentPlans.map((plan) => (
                      <RecordCard
                        key={plan.id || plan._id}
                        title={Array.isArray(plan.treatmentTypes) ? plan.treatmentTypes.join(", ") : "Treatment plan"}
                        subtitle={`From ${formatDate(plan.fromDate)} to ${formatDate(plan.toDate)}`}
                      >
                        <div className="mt-2">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(plan.status || "active")}`}>
                            {formatStatusLabel(plan.status || "active")}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          Total: {formatMoney(plan.totalAmount)} | Advance: {formatMoney(plan.advanceAmount)} | Balance: {formatMoney(plan.balanceAmount)}
                        </p>
                        {plan.sessionDays?.length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {plan.sessionDays.map((day) => {
                              const isDone = day.status === "done";

                              return (
                                <span
                                  key={day.id || day.date}
                                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                    isDone
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-amber-50 text-amber-700"
                                  }`}
                                >
                                  {day.date}: {isDone ? "Done" : "Not done"}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                      </RecordCard>
                    ))}
                  </RecordList>
                </Panel>
              )}

              {activeTab === "payments" && (
                <Panel title="Payment Details" subtitle="Payment records from your OPW care plan.">
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Total Paid
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{formatMoney(totalPaidAmount)}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Outstanding
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{formatMoney(outstandingBalance)}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Entries
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{payments.length}</p>
                    </div>
                  </div>
                  <RecordList emptyText="No payment details added yet.">
                    {payments.map((payment, index) => (
                      <RecordCard
                        key={payment.id || payment._id || index}
                        title={formatMoney(payment.amount)}
                        subtitle={[
                          payment.method || "Payment",
                          payment.treatmentName,
                          `Date: ${formatDate(payment.createdAt)}`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      />
                    ))}
                  </RecordList>
                </Panel>
              )}

              {activeTab === "orders" && (
                <Panel
                  title="My Orders"
                  subtitle="All shop orders placed from your patient account will appear here."
                >
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Orders
                      </p>
                      <p className="mt-2 text-xl font-semibold text-slate-950">{shopOrders.length}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Latest Status
                      </p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">
                        {latestOrder ? formatStatusLabel(latestOrder.status) : "No order yet"}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Latest Total
                      </p>
                      <p className="mt-2 text-sm font-semibold text-slate-950">
                        {latestOrder ? formatMoney(latestOrder.totalAmount) : formatMoney(0)}
                      </p>
                    </div>
                  </div>
                  <RecordList emptyText="No shop orders placed yet.">
                    {shopOrders.map((order) => (
                      <RecordCard
                        key={order.id}
                        title={`${order.orderNumber} | ${formatMoney(order.totalAmount)}`}
                        subtitle={[
                          `Ordered on ${formatDate(order.createdAt)}`,
                          `${order.totalQuantity || 0} item(s)`,
                        ].join(" | ")}
                      >
                        <div className="mt-2">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
                            {formatStatusLabel(order.status || "pending")}
                          </span>
                        </div>
                        {(order.items || []).length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {order.items.map((item) => (
                              <span
                                key={item.id}
                                className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700"
                              >
                                {item.productName} x {item.quantity}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {order.note ? (
                          <p className="mt-3 rounded-2xl bg-slate-100 px-3 py-2 text-sm text-slate-600">
                            Note: {order.note}
                          </p>
                        ) : null}
                      </RecordCard>
                    ))}
                  </RecordList>
                </Panel>
              )}
            </>
          )}
        </div>

        {previewItem ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
            <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-sky-600">Therapy Preview</p>
                  <h3 className="truncate text-xl font-semibold text-slate-900">
                    {previewItem.title || previewItem.fileName || "Therapy File"}
                  </h3>
                  <p className="mt-1 truncate text-sm text-slate-500">
                    {previewItem.fileName}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closePreview}
                  className="rounded-xl border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
                  title="Close preview"
                >
                  <CircleX size={18} />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-4">
                {(previewItem.resourceType === "image" ||
                  previewItem.resourceType === "gif") ? (
                  <div className="flex min-h-[60vh] items-center justify-center">
                    <img
                      src={previewItem.objectUrl}
                      alt={previewItem.title || previewItem.fileName || "Therapy File"}
                      className="max-h-[75vh] max-w-full rounded-2xl bg-white object-contain shadow-sm"
                    />
                  </div>
                ) : null}

                {previewItem.resourceType === "video" ? (
                  <div className="flex min-h-[60vh] items-center justify-center">
                    <video
                      src={previewItem.objectUrl}
                      controls
                      className="max-h-[75vh] w-full rounded-2xl bg-black shadow-sm"
                    />
                  </div>
                ) : null}

                {previewItem.mimeType === "application/pdf" ? (
                  <iframe
                    src={previewItem.objectUrl}
                    title={previewItem.title || previewItem.fileName || "Therapy File"}
                    className="h-[75vh] w-full rounded-2xl border border-slate-200 bg-white"
                  />
                ) : null}

                {!(
                  previewItem.resourceType === "image" ||
                  previewItem.resourceType === "gif" ||
                  previewItem.resourceType === "video" ||
                  previewItem.mimeType === "application/pdf"
                ) ? (
                  <div className="flex min-h-[60vh] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-6 text-center">
                    <div className="rounded-3xl bg-slate-100 p-4 text-slate-700">
                      <FileText size={32} />
                    </div>
                    <h4 className="mt-5 text-xl font-semibold text-slate-900">
                      Preview not available for this file type
                    </h4>
                    <p className="mt-2 max-w-md text-sm text-slate-500">
                      This therapy file cannot be previewed directly inside the
                      patient panel.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </PublicLayout>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-slate-500">{subtitle}</p>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function RecordList({ emptyText, children }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : children;

  if (!items || (Array.isArray(items) && !items.length)) {
    return <p className="rounded-2xl bg-slate-50 px-4 py-5 text-sm text-slate-500">{emptyText}</p>;
  }

  return <div className="grid gap-3">{items}</div>;
}

function RecordCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="font-semibold text-slate-950">{title}</p>
      <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>
      {children}
    </div>
  );
}

function HighlightCard({ icon: Icon, title, value, helper }) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-white p-2.5 text-sky-700 shadow-sm">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            {title}
          </p>
          <p className="mt-2 truncate text-base font-semibold text-slate-950">{value}</p>
          <p className="mt-1 text-sm leading-6 text-slate-500">{helper}</p>
        </div>
      </div>
    </div>
  );
}
